// Castintech dashboard — Telegram view + send modal modulu.
// Faz 9b PR7 — 7 fonksiyon (~140 satir):
// - loadTelegramSessions, renderTelegram (cards + sort + active/resolved badge)
// - openTelegramSend, closeTelegramSend, updateTgChars, tgSendKeydown,
//   submitTelegramSend (modal + API call)
//
// State erisimi: state.js direct import (state.telegramSessions, state.tgOpenChatId, vs.)
// Utils: utils.js (escHtml/escAttr/ago/getGem).
// Api: api.js (getTelegramSessions/postTelegramSend).
// Toast/openDetail: window.X (PR9'da module'e tasinacak).

import { state } from '../state.js';
import * as Api from '../api.js';
import { escHtml, escAttr, ago, getGem, $, asEl } from '../utils.js';
import { toast } from '../ui/toast.js';
import { openDetail } from '../ui/detail-panel.js';

// ── Liste fetch + render ─────────────────────────────────────────────

export async function loadTelegramSessions() {
  const sub = document.getElementById('telegram-sub');
  if (sub) sub.textContent = 'yükleniyor...';
  try {
    const r = await Api.getTelegramSessions();
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const j = await r.json();
    state.telegramSessions = j.sessions || [];
    renderTelegram();
  } catch (e) {
    const msg = (e as Error).message;
    console.warn('loadTelegramSessions failed:', msg);
    if (sub) sub.textContent = 'hata: ' + msg;
    const grid = document.getElementById('telegram-grid');
    if (grid) grid.innerHTML = `<div class="empty-state" style="grid-column:1/-1"><div class="empty-icon">⚠️</div><div class="empty-title">Telegram oturumları yüklenemedi</div></div>`;
  }
}

export function renderTelegram() {
  const sub = document.getElementById('telegram-sub');
  const grid = document.getElementById('telegram-grid');
  const badge = document.getElementById('telegram-badge');
  if (!grid) return;
  if (!state.telegramSessions.length) {
    if (sub) sub.textContent = 'oturum bulunamadı';
    grid.innerHTML = `<div class="empty-state" style="grid-column:1/-1"><div class="empty-icon">📞</div><div class="empty-title">Aktif Telegram oturumu yok</div><div class="empty-sub">Kullanıcılar bot'a yazınca burada görünür</div></div>`;
    if (badge) badge.style.display = 'none';
    return;
  }
  const active = state.telegramSessions.filter(s => !s.resolved).length;
  const resolved = state.telegramSessions.length - active;
  if (sub) sub.textContent = `${state.telegramSessions.length} oturum — ${active} aktif · ${resolved} çözüldü`;
  if (badge) {
    if (active > 0) {
      badge.textContent = String(active);
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  }

  const sorted = [...state.telegramSessions].sort((a, b) => {
    if (a.resolved !== b.resolved) return a.resolved ? 1 : -1;
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });

  grid.innerHTML = sorted.map((s, i) => {
    const gem = getGem(s.agent_name || '');
    const issue = state.tasks.find(t => t.id === s.issue_id);
    const issueLabel = issue ? `${issue.identifier || ''} ${escHtml((issue.title || '').substring(0, 40))}` : '—';
    const issueClick = issue ? `data-action="open-detail" data-task-id="${escAttr(issue.id)}" style="cursor:pointer"` : '';
    const stateBadge = s.resolved
      ? `<span class="tg-card-badge resolved">çözüldü</span>`
      : `<span class="tg-card-badge active">aktif</span>`;
    const sendBtn = s.resolved
      ? `<button class="tg-card-btn" disabled title="Bu oturum çözüldü">📞 Mesaj Gönder</button>`
      : `<button class="tg-card-btn" data-action="tg-send" data-chat-id="${escAttr(s.chat_id)}" data-agent-name="${escAttr(s.agent_name || '')}">📞 Mesaj Gönder</button>`;
    return `
      <div class="tg-card ${s.resolved ? 'resolved' : ''}" style="animation-delay:${i * 30}ms">
        <div class="tg-card-head">
          <div class="tg-card-title"><span class="${gem.cls}" style="display:inline-block;width:10px;height:10px;border-radius:50%;background:linear-gradient(135deg,${gem.bg});"></span>${escHtml(gem.name || s.agent_name || '?')}</div>
          ${stateBadge}
        </div>
        <div class="tg-card-meta">
          <div><b>chat:</b> <span class="tg-card-chat">${escHtml(String(s.chat_id))}</span></div>
          <div><b>issue:</b> ${issue ? `<span class="tg-card-issue" ${issueClick}>${issueLabel}</span>` : '<span style="color:var(--muted)">—</span>'}</div>
          <div><b>başlangıç:</b> ${ago(s.created_at)} önce</div>
        </div>
        <div class="tg-card-actions">${sendBtn}</div>
      </div>`;
  }).join('');
}

// ── Send modal ───────────────────────────────────────────────────────

export function openTelegramSend(chatId: string, agentName: string): void {
  state.tgOpenChatId = String(chatId);
  state.tgOpenAgentName = agentName || '';
  const gem = getGem(agentName || '');
  const title = $('tg-modal-title');
  if (title) title.textContent = `📞 ${gem.name || agentName || 'Telegram'} oturumu`;
  const subEl = $('tg-modal-sub');
  if (subEl) subEl.textContent = `chat ${chatId}`;
  const ta = $<HTMLTextAreaElement>('tg-send-input');
  if (ta) ta.value = '';
  updateTgChars();
  $('tg-send-modal')?.classList.add('visible');
  setTimeout(() => ta?.focus(), 50);
}

export function closeTelegramSend(): void {
  $('tg-send-modal')?.classList.remove('visible');
  state.tgOpenChatId = null;
  state.tgOpenAgentName = null;
  const ta = $<HTMLTextAreaElement>('tg-send-input');
  if (ta) ta.value = '';
  updateTgChars();
}

export function updateTgChars(): void {
  const ta = $<HTMLTextAreaElement>('tg-send-input');
  const lbl = $('tg-send-chars');
  if (!ta || !lbl) return;
  const n = (ta.value || '').length;
  lbl.textContent = `${n} / 4000`;
  lbl.style.color = n > 4000 ? 'var(--red, #ef4444)' : 'var(--muted)';
}

export function tgSendKeydown(e: KeyboardEvent): void {
  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
    e.preventDefault();
    submitTelegramSend();
  }
}

export async function submitTelegramSend(): Promise<void> {
  if (state.tgSendInFlight || !state.tgOpenChatId) return;
  const ta = $<HTMLTextAreaElement>('tg-send-input');
  const text = (ta?.value || '').trim();
  if (!text) { toast('Mesaj boş olamaz', 'error'); return; }
  if (text.length > 4000) { toast('Mesaj 4000 karakteri geçemez', 'error'); return; }
  state.tgSendInFlight = true;
  const btn = $<HTMLButtonElement>('tg-send-submit');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Gönderiliyor...';
  }
  try {
    const r = await Api.postTelegramSend(state.tgOpenChatId, text);
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      throw new Error(j.error || ('HTTP ' + r.status));
    }
    toast('✓ Telegram mesajı gönderildi', 'success');
    closeTelegramSend();
  } catch (e) {
    toast('Gönderilemedi: ' + (e as Error).message, 'error');
  } finally {
    state.tgSendInFlight = false;
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Gönder';
    }
  }
}

// ── Faz 10 PR2 — Telegram send modal statik bind ────────────────────
// HTML inline click/input/keydown handler'lari silindi;
// main.js bootstrap bu fn'i cagirir.
let _tgSendBound = false;
export function bindTelegramSendStatic() {
  if (_tgSendBound) return;

  // Overlay click → kapat (yalnizca overlay'in kendisine tiklandiysa)
  const overlay = document.getElementById('tg-send-modal');
  if (overlay) overlay.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeTelegramSend();
  });

  // Textarea — input + keydown
  const ta = document.getElementById('tg-send-input');
  if (ta) {
    ta.addEventListener('input', () => updateTgChars());
    ta.addEventListener('keydown', (e) => tgSendKeydown(e));
  }

  // Cancel button (#tg-cancel) → closeTelegramSend
  const cancelBtn = document.getElementById('tg-cancel');
  if (cancelBtn) cancelBtn.addEventListener('click', () => closeTelegramSend());

  // Submit button (#tg-send-submit) → submitTelegramSend
  const submitBtn = document.getElementById('tg-send-submit');
  if (submitBtn) submitBtn.addEventListener('click', () => submitTelegramSend());

  _tgSendBound = true;
}

// Faz 10 PR6 — Telegram view static + grid delegation.
let _tgRefreshBound = false;
export function bindTelegramRefresh() {
  if (_tgRefreshBound) return;
  const btn = document.getElementById('tg-refresh-btn');
  if (btn) btn.addEventListener('click', () => loadTelegramSessions());
  _tgRefreshBound = true;
}

let _tgGridDelegated = false;
export function bindTelegramGridDelegation() {
  if (_tgGridDelegated) return;
  const grid = document.getElementById('telegram-grid');
  if (!grid) return;
  grid.addEventListener('click', (e) => {
    const el = asEl(e.target)?.closest<HTMLElement>('[data-action]');
    if (!el || !grid.contains(el)) return;
    if (el.dataset.action === 'open-detail' && el.dataset.taskId) {
      openDetail(el.dataset.taskId);
    } else if (el.dataset.action === 'tg-send' && el.dataset.chatId) {
      openTelegramSend(el.dataset.chatId, el.dataset.agentName || '');
    }
  });
  _tgGridDelegated = true;
}
